import { useEffect, useRef, useState } from "react";

// Animates a numeric value from 0 up to target when it enters the viewport.
// Value may include non-numeric suffix (e.g. "36", "+50%", "3 + 2").
const zeroed = (value) => String(value ?? "").replace(/-?\d+(?:\.\d+)?/, (m) => (m.includes(".") ? "0.0" : "0"));

export default function AnimatedCounter({ value, duration = 1400, className = "" }) {
  const [display, setDisplay] = useState(() => zeroed(value));
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    // Extract first numeric segment
    const match = String(value).match(/-?\d+(?:\.\d+)?/);
    if (!match) {
      setDisplay(value);
      return;
    }
    const numStr = match[0];
    const target = Number(numStr);
    const before = String(value).slice(0, match.index);
    const after = String(value).slice(match.index + numStr.length);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const from = 0;
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              const val = from + (target - from) * eased;
              const nice = Number.isInteger(target) ? Math.round(val) : val.toFixed(1);
              setDisplay(`${before}${nice}${after}`);
              if (t < 1) requestAnimationFrame(tick);
              else setDisplay(value);
            };
            requestAnimationFrame(tick);
            observer.disconnect();
          }
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return <span ref={ref} className={className}>{display}</span>;
}
